/**
 * Keypoint domain objects representing anatomical landmarks.
 */

import { distance } from './geometry';
import type { Vec3, IKeypointData } from './types';

/**
 * Interface for a keypoint (anatomical landmark).
 */
export interface IKeypoint {
    readonly name: string;
    readonly position: Vec3;
    readonly description?: string;
}

/**
 * Represents an anatomical landmark position.
 * Immutable after construction.
 */
export class Keypoint implements IKeypoint {
    constructor(
        readonly name: string,
        readonly position: Vec3,
        readonly description?: string
    ) {
        Object.freeze(this);
    }

    /**
     * Check if this keypoint is within a given 3D distance of a position.
     * Note: For clinically meaningful distance along vessel paths,
     * use Centerline.getLengthBetween() instead.
     */
    isWithinDistance(position: Vec3, maxDistance: number): boolean {
        return distance(this.position, position) <= maxDistance;
    }

    /**
     * Create a Keypoint from raw API data.
     */
    static fromData(data: IKeypointData): Keypoint {
        return new Keypoint(data.name, data.position, data.description);
    }
}

/**
 * Factory for creating keypoints from vascular description data.
 */
export class KeypointFactory {
    /**
     * Create all keypoints from vascular description data.
     */
    static createFromVascularDescription(keypoints: readonly IKeypointData[]): Map<string, Keypoint> {
        const result = new Map<string, Keypoint>();

        for (const kp of keypoints) {
            result.set(kp.name, Keypoint.fromData(kp));
        }

        return result;
    }
}
